import { pushRoute } from '../../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { omit } from 'lodash';
import {
  deleteStakeholder,
  finishUpdateStakeholderDraft,
  selectStakeholder,
  selectStakeholderDraft,
  selectStakeholderDraftId,
  updateStakeholderDraft,
} from '../../../modules/stakeholders.js';
import StepInformationForm from '../../../components/projects/Stakeholders/StakeholderForms/StepInformationForm.jsx';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import { getMultipleEditorsData } from '../../../components/editor/defaultOptions.js';
import {
  navigateToStepGroupParticipants,
  navigateToStepStakeholder,
  onCreateUpdateCancel,
  onReset,
  stakeholdersUrl,
  stakeholderUrl,
} from './utils/index.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import StakeholderFormHeader from '../../../components/headers/PageHeaders/Project/Stakeholders/StakeholderFormHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save stakeholder');

const mapStateToProps = (state, ownProps) => ({
  editing: !!ownProps.stakeholderId,
  stakeholderId: ownProps.stakeholderId,
  draftId: selectStakeholderDraftId(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
  stakeholderDraft: selectStakeholderDraft(selectStakeholderDraftId(state))(
    state
  ),
  stakeholder: omit(selectStakeholder(state, ownProps), [
    'id',
    'influencePosition',
    'interestPosition',
  ]),
});

const mapDispatchToProps = {
  deleteStakeholder,
  retrieveProject,
  updateStakeholderDraft,
  finishUpdateStakeholderDraft,
};

function StepInformationFormContainer({
  slug,
  deleteStakeholder,
  projectId,
  draftId,
  project,
  retrieveProject,
  editing,
  stakeholderDraft,
  updateStakeholderDraft,
  finishUpdateStakeholderDraft,
  stakeholderId,
  stakeholder,
}) {
  const [fields, setFields] = useState(null);

  const goNext = () =>
    navigateToStepGroupParticipants(editing, projectId, stakeholderId, slug);
  const goBack = () =>
    navigateToStepStakeholder(editing, projectId, stakeholderId, slug);

  const onDelete = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this stakeholder profile?'
      )
    )
      return;
    deleteStakeholder(stakeholderId).then(() =>
      pushRoute(stakeholdersUrl, { projectId })
    );
  };

  const generateFields = () => {
    const filteredFields = project.stakeholderFields
      .filter((field) => field.allowEditor)
      .sort((a, b) => Number(a.priority) - Number(b.priority));

    const fields = filteredFields.map((item) => ({
      label: item.name,
      key: item.key,
      isEditor: item.editor,
      editorInstance: null,
    }));
    setFields(fields);
  };

  const updateEditorInstance = (editorObject, index) => {
    setFields((prevFields) => {
      const updatedObj = { ...prevFields[index], editorInstance: editorObject };
      return [
        ...prevFields.slice(0, index),
        updatedObj,
        ...prevFields.slice(index + 1),
      ];
    });
  };

  const onSubmit = (values) => {
    const { finish } = values;

    const editorInstances = fields.filter((field) => field.editorInstance);

    const composedData = (editorData) => {
      const editorFields = editorInstances.reduce((acc, item, index) => {
        acc[item.key] = JSON.stringify(editorData[index]);
        return acc;
      }, {});
      const nonEditorFields = fields
        .filter((field) => !field.isEditor)
        .reduce((acc, item) => {
          acc[item.key] = values[item.key] || null;
          return acc;
        }, {});

      return {
        ...editorFields,
        ...nonEditorFields,
      };
    };
    return getMultipleEditorsData(
      editorInstances.map((item) => item.editorInstance)
    ).then((editorData) => {
      const data = composedData(editorData);
      const hasData = Object.keys(data).length > 0;

      if (!hasData) {
        return finish
          ? pushRoute(stakeholderUrl, { projectId, stakeholderId: draftId })
          : goNext();
      }

      return (
        finish
          ? finishUpdateStakeholderDraft(draftId, data)
          : updateStakeholderDraft(draftId, data)
      )
        .then(({ result }) =>
          finish
            ? pushRoute(stakeholderUrl, { projectId, stakeholderId: result })
            : goNext()
        )
        .catch(mapErrors);
    });
  };

  const onCancel = () =>
    onCreateUpdateCancel(draftId, deleteStakeholder, projectId, slug);

  useEffect(() => {
    !draftId && onReset(editing, projectId, stakeholderId, slug);
    retrieveProject(projectId);
  }, []);

  useEffect(() => {
    if (project && !fields) {
      generateFields();
    }
  }, [project]);

  useEffect(() => {}, [project]);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        onDelete={onDelete}
        editing={editing}
        subject="Stakeholder Profile"
        typeStep="Information"
      >
        {project && (editing ? Object.keys(stakeholder).length : true) ? (
          <StakeholderFormHeader
            slug={slug}
            project={project}
            editing={editing}
            stakeholder={stakeholder}
            stakeholderId={stakeholderId}
          />
        ) : null}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {project && draftId ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      onSubmit={onSubmit}
                      onCancel={onCancel}
                      initialValues={draftId ? stakeholderDraft : []}
                      updateEditorInstance={updateEditorInstance}
                      fields={fields || []}
                      onBack={goBack}
                      component={StepInformationForm}
                    />
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepInformationFormContainer);
